import { useEffect, useRef, useState } from "preact/hooks";
import "./app.css";
import { Account, initWidget, loadAccount, logout } from "./xsolla";
import { PremiumDays, PremiumCheck, PremiumPlan } from "./premium-plan";
import { useT } from "./i18n";
import { v8 } from "./v8-config";
import { reachGoal } from "./ym";
import { lStorage } from "./lstorage";


const params = new URLSearchParams(location.search);
const sso = params.get("sso") ?? lStorage.getItem("query_sso");
const ssoSig = params.get("sig") ?? lStorage.getItem("query_sig");

if (params.get("sso") !== undefined && params.get("sig") !== undefined) {
    lStorage.setItem("query_sso", params.get("sso")!);
    lStorage.setItem("query_sig", params.get("sig")!);
} else {
    lStorage.removeItem("query_sso");
    lStorage.removeItem("query_sig");
}

const isSSO = typeof sso === "string" && sso.length > 0;

export function App() {
    const loginWidgetElement = useRef<HTMLDivElement>(null);
    const [account, setAccount] = useState<Account | null | undefined>(undefined);
    const [token, setToken] = useState<string>("-----");
    const [premium, setPremium] = useState<boolean>(false);
    const [expires, setExpires] = useState<number>(0);
    const [subscription, setSubscription] = useState<string>("");
    const [generating, setGenerating] = useState<boolean>(false);
    const t = useT();

    useEffect(() => {
        (async function() {
            const account = await loadAccount();
            if (account) {
                const entry = await (await fetch(v8 + "/token/get?id=" + account.id +
                    "&token=" + account.token.access_token)).json();
                if (entry.token) {
                    setToken(entry.token);
                    setPremium(entry.premium);
                    setExpires(entry.expires);
                    setSubscription(entry.subscription);
                }
            }
            if (isSSO && account) {
                try {
                    lStorage.removeItem("query_sso");
                    lStorage.removeItem("query_sig");

                    const { success, payload, payloadSig, url } = await (await fetch(v8 + "/discourse?sso=" + sso +
                        "&sig=" + ssoSig +
                        "&id=" + encodeURIComponent(account.id) +
                        "&email=" + encodeURIComponent(account.email) +
                        "&name=" + encodeURIComponent(account.name ?? account.email.split("@")[0]) +
                        "&picture=" + encodeURIComponent(account.picture ?? "") +
                        "&token=" + account.token.access_token)).json();
                    console.log("res", payload);

                    location.href = url + "?sso=" + payload + "&sig=" + payloadSig;

                    if (!success) {
                        throw new Error("Failed to authenticate");
                    }
                } catch (e) {
                    console.error(e);
                    location.href = "/";
                }
            } else {
                setAccount(account);
            }
        })().catch(console.error);
    }, []);

    useEffect(() => {
        if (account === null) {
            if (loginWidgetElement.current !== null) {
                const widget = initWidget(loginWidgetElement.current);
                widget.show();
            }
        }
    }, [account === null, loginWidgetElement]);

    function regen() {
        if (generating) {
            return;
        }

        (async () => {
            reachGoal("gen");
            if (account?.token.access_token) {
                setGenerating(true);
                try {
                    const payload = await (await fetch(v8 + "/token/gen?token=" + account.token.access_token)).json();
                    if (payload.token && payload.token.length === 5) {
                        setToken(payload.token);
                        const profile = await (await fetch(v8 + "/token/get?id=" + payload.token)).json();
                        if (profile.premium) {
                            setPremium(profile.premium);
                            setExpires(profile.expires);
                            setSubscription(profile.subscription);
                        }
                    }
                } finally {
                    setGenerating(false);
                }
            }
        })().catch(console.error);
    }

    function copy() {
        if (!navigator.clipboard) {
            return;
        }

        navigator.clipboard.writeText(token);
    }

    const name = account?.name ?? account?.email ?? "";

    return (
        <div class="w-full px-4 pt-16 pb-32">
            <article class="prose lg:prose-xl mx-auto">
                <h1><img class="size-24 inline-block logo mr-4" src="images/logo-192.png" />
                    {isSSO ? t("sso_title") : t("title")}
                </h1>
                {account !== null && account !== undefined &&
                    <div class="flex flex-row items-baseline">
                        {t("hello")}{name.length > 0 ? ", " + name : ""}!
                        <div class="ml-2 text-xs flex flex-row items-baseline">
                            (<span class="link" onClick={logout}>{t("logout")}</span>)
                        </div>
                    </div>
                }
                {(account === null || account === undefined) &&
                    <div>{t("hello")}!</div>
                }
                {isSSO ? t("sso_greetings") : t("greetings")}
                {account === null && <>
                    <div class="mt-8">{t("please_login")}</div>
                </>}
                {account === undefined && <>
                    <p><span className="loading loading-ring loading-lg"></span></p>
                </>}
                <div class="" ref={loginWidgetElement}></div>
                {account !== null && account !== undefined && <>
                    <div class="mt-4 mb-2 flex flex-row items-baseline gap-2 font-bold text-primary">
                        {t("key")}:
                    </div>
                    {!generating && <div class="flex flex-row flex-wrap items-center">
                        <div className="stats shadow">
                            <div className="stat">{token[0]}</div>
                            <div className="stat">{token[1]}</div>
                            <div className="stat">{token[2]}</div>
                            <div className="stat">{token[3]}</div>
                            <div className="stat">{token[4]}</div>
                        </div>
                        <div class="btn btn-primary btn-ghost" onClick={copy}>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                stroke-width="1.5" stroke="currentColor" class="size-6">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166
                                        1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0
                                        1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128
                                        1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0
                                        1 4.5 19.5V6.257c0-1.108.806-2.057
                                        1.907-2.185a48.208 48.208 0 0 1 1.927-.184" />
                            </svg>
                        </div>
                        <div class="btn btn-primary btn-ghost" disabled={generating} onClick={regen}>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                stroke-width="1.5" stroke="currentColor" class="size-6">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985
                                    19.644v-4.992m0 0h4.992m-4.993 0 3.181
                                    3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0
                                    0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                            </svg>
                        </div>
                    </div>}
                    {generating &&
                        <p><span className="loading loading-ring loading-lg"></span></p>}
                    <div class="mt-16 mb-2">
                        {t("subscription")}:
                    </div>
                    <SubscriptionStatus premium={premium} expire={expires} subscription={subscription} />
                    <div class="mt-4 mb-2">
                        {t("plan")}:
                    </div>
                    <div class="flex flex-row flex-wrap gap-4">
                        <PremiumDays account={account} price={2}
                            description="30 days" title="thirty_days_pass" sku="pass30" />
                        <PremiumDays account={account} price={6}
                            description="6 months" title="six_months_pass" sku="pass180" />
                        <PremiumPlan account={account} subscription={subscription} />
                    </div>
                    <div>
                        <h3>{t("premium_options")}</h3>
                        <div class="flex flex-col mt-4 font-bold">
                            <div class="premium-plan-highlight">
                                <PremiumCheck />
                                <div>+&nbsp;{t("cloud_saves")}</div>
                            </div>
                            <div class="premium-plan-highlight">
                                <PremiumCheck />
                                <div>+&nbsp;{t("private_rooms")}</div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <h3>{t("can_i_share_key")}</h3>
                        {t("can_i_share_content")}
                    </div>
                    {/* <div>
                        <h3>{t("help")}</h3>
                        <a href="https://js-dos.com/subscription.html" target="_blank">{t("help_content")}</a>
                    </div> */}
                </>}
            </article>
        </div>
    );
}

function SubscriptionStatus(props: { premium: boolean, expire: number, subscription: string }) {
    const { premium, expire, subscription } = props;
    const t = useT();
    if (premium && subscription.length > 0) {
        return <span class="font-bold text-green-500">
            {t("active")}
        </span>;
    }
    if (premium && expire > 0) {
        return <span class="font-bold text-green-500">
            {t("active")} - {Math.ceil(expire / (24 * 60 * 60))} {t("days")}
        </span>;
    }

    return <span class="font-bold text-red-500">
        {t("notactive")}
    </span>;
}
